/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Reusable Shader Effect Processor Framework
   Eliminates boilerplate for GPU shader effects while managing
   lifecycle, context validation, dimensions, and typed ImageValues.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeProcessor } from '../../execution/types';
import type { ShaderUniforms, TextureHandle } from '../types';
import { createImageValue, getMediaDimensions, getTextureFromMedia } from '../media';

export interface ShaderEffectConfig {
  readonly fragmentShader: string;
  readonly getUniforms: (
    inputs: Record<string, unknown>,
    params: Record<string, unknown>,
    dimensions: { width: number; height: number }
  ) => ShaderUniforms;
  readonly calculateDimensions?: (
    inputs: Record<string, unknown>,
    params: Record<string, unknown>
  ) => { width: number; height: number };
  readonly primaryInputPort?: string;
  readonly primaryOutputPort?: string;
}

export function createShaderProcessor(config: ShaderEffectConfig): NodeProcessor {
  const primaryInputPort = config.primaryInputPort ?? 'image';
  const primaryOutputPort = config.primaryOutputPort ?? 'output';

  return async (inputs, params, context) => {
    if (context.backend.name !== 'webgl2' || !context.capabilities.webgl2) {
      throw new Error(
        'This GPU effect requires WebGL2 hardware acceleration, which is not available in this environment.'
      );
    }

    const inputRaw = inputs[primaryInputPort];
    const inputTex = getTextureFromMedia(inputRaw);
    if (!inputTex || !inputTex.nativeHandle) {
      return { [primaryOutputPort]: null };
    }

    if (context.signal.aborted) {
      throw new DOMException('Execution aborted', 'AbortError');
    }

    const inputDimensions = getMediaDimensions(inputRaw);
    const dimensions = config.calculateDimensions
      ? config.calculateDimensions(inputs, params)
      : inputDimensions;

    const width = Math.max(1, Math.round(dimensions.width));
    const height = Math.max(1, Math.round(dimensions.height));

    const uniforms = config.getUniforms(inputs, params, { width, height });

    const outputTexture = await context.backend.executeShaderPass(
      {
        fragmentSource: config.fragmentShader,
        uniforms,
      },
      width,
      height
    );

    context.allocateResource(outputTexture);

    const imageValue = createImageValue(outputTexture, {
      width,
      height,
      sourceType: 'processed',
    });

    return {
      [primaryOutputPort]: imageValue,
    };
  };
}

export interface MultiPassStep {
  readonly fragmentShader: string;
  readonly getUniforms: (
    stepInputTexture: TextureHandle,
    inputs: Record<string, unknown>,
    params: Record<string, unknown>,
    dimensions: { width: number; height: number }
  ) => ShaderUniforms;
}

export interface MultiPassConfig {
  readonly steps: readonly MultiPassStep[];
  readonly calculateDimensions?: (
    inputs: Record<string, unknown>,
    params: Record<string, unknown>
  ) => { width: number; height: number };
  readonly primaryInputPort?: string;
  readonly primaryOutputPort?: string;
}

/**
 * Creates a multipass GPU effect processor (e.g. separable Gaussian Blur).
 */
export function createMultiPassProcessor(config: MultiPassConfig): NodeProcessor {
  const primaryInputPort = config.primaryInputPort ?? 'image';
  const primaryOutputPort = config.primaryOutputPort ?? 'output';

  return async (inputs, params, context) => {
    if (context.backend.name !== 'webgl2' || !context.capabilities.webgl2) {
      throw new Error(
        'This GPU effect requires WebGL2 hardware acceleration, which is not available in this environment.'
      );
    }

    const inputRaw = inputs[primaryInputPort];
    const inputTex = getTextureFromMedia(inputRaw);
    if (!inputTex || !inputTex.nativeHandle) {
      return { [primaryOutputPort]: null };
    }

    if (context.signal.aborted) {
      throw new DOMException('Execution aborted', 'AbortError');
    }

    const inputDimensions = getMediaDimensions(inputRaw);
    const dimensions = config.calculateDimensions
      ? config.calculateDimensions(inputs, params)
      : inputDimensions;

    const width = Math.max(1, Math.round(dimensions.width));
    const height = Math.max(1, Math.round(dimensions.height));

    let currentTexture: TextureHandle = inputTex;
    const intermediateTextures: TextureHandle[] = [];

    for (let i = 0; i < config.steps.length; i++) {
      if (context.signal.aborted) {
        // Dispose any allocated intermediate textures on abort
        for (const intermediate of intermediateTextures) {
          intermediate.dispose();
        }
        throw new DOMException('Execution aborted', 'AbortError');
      }

      const step = config.steps[i];
      const uniforms = step.getUniforms(currentTexture, inputs, params, { width, height });

      const passOutput = await context.backend.executeShaderPass(
        {
          fragmentSource: step.fragmentShader,
          uniforms,
        },
        width,
        height
      );

      if (i < config.steps.length - 1) {
        intermediateTextures.push(passOutput);
      } else {
        context.allocateResource(passOutput);
      }
      currentTexture = passOutput;
    }

    // Immediately clean up intermediate passes (e.g. horizontal pass in 2-pass blur)
    for (const intermediate of intermediateTextures) {
      try {
        intermediate.dispose();
      } catch {
        // Ignore safe cleanup
      }
    }

    const imageValue = createImageValue(currentTexture, {
      width,
      height,
      sourceType: 'processed',
    });

    return {
      [primaryOutputPort]: imageValue,
    };
  };
}
